'use client';
import React, { Fragment, Suspense } from 'react';
import PageHeader from '@mae/components/core/PageHeader';
import { discography, DiscographyGroup, DiscographyView } from '@mae/store/discography';
import { IDiscography, IUnresolvedRelease } from '@mae/misc/discography/types';
import discog from '@mae/data/discography-vimae.json'
import { groupReleasesByKey } from '@mae/misc/discography/utils';
import ReleaseCard from '@mae/components/feature/discography/ReleaseCard';
import ReleaseRow from '@mae/components/feature/discography/ReleaseRow';
import { useAtom } from 'jotai';

export default function MusicPageClient() {
  const [discogStore, setDiscography] = useAtom(discography);
  const {group, view} = discogStore;

  const releases = (discog as IDiscography).releases;

  const getGroupings = () => {
    switch(group) {
      case DiscographyGroup.YEAR:
        return groupReleasesByKey(releases, "year");

      case DiscographyGroup.TYPE:
        const groupedByType = groupReleasesByKey(releases, "type");
        const typeOrder = ["Album", "EP", "Single"]

        const ordered = Object
          .keys(groupedByType)
          .sort((a: string, b: string) => {
            const posA = typeOrder.indexOf(a) || typeOrder.length
            const posB = typeOrder.indexOf(b) || typeOrder.length

            return posB - posA
          })
          .reduce((obj: any, title: string) => {
            const temp = {
              ...obj
            }
            temp[title] = groupedByType[title];
            return temp
          }, {})

        return ordered

      default:
        return {
          "All": releases
        }
    }
  }

  return (
    <Fragment>
      <div className="flex flex-col sm:flex-row justify-between gap-2">
        <PageHeader title="💿 discography"></PageHeader>

        <div className="flex flex-row items-center gap-4 overflow-x-auto">
          <div className="space-x-2 text-sm flex">
            <label
              htmlFor="group"
              className="flex items-center gap-1 flex-shrink-0"
            >
              <i className="bx bxs-category bx-xs" />
              Group by
            </label>
            <select
              name="group"
              id="cars"
              className="bg-ctp-surface0 p-2 rounded-lg"
              defaultValue={group}
              onChange={(e: any) =>
                setDiscography({ ...discogStore, group: e.target.value })
              }
            >
              <option value="none">None</option>
              <option value="year">Release Year</option>
              <option value="type">Release Type</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              className="bg-ctp-surface0 p-2 rounded-lg flex items-center justify-center h-fit"
              onClick={() =>
                setDiscography({
                  ...discogStore,
                  view: DiscographyView.GRIDLIST,
                })
              }
            >
              <i className="bx bxs-grid-alt bx-sm"></i>
            </button>
            <button
              className=" p-2 rounded-lg flex items-center justify-center h-fit"
              onClick={() =>
                setDiscography({ ...discogStore, view: DiscographyView.ROW })
              }
            >
              <i className="bx bxs-objects-horizontal-left bx-sm"></i>
            </button>
          </div>
        </div>
      </div>

      {Object.keys(getGroupings())
        .reverse()
        .map((groupTitle) => {
          const groupContents = getGroupings()[groupTitle];
          return (
            <div className='' key={groupTitle}>
              <h2 className="text-2xl font-bold mb-4">{groupTitle}</h2>
              <div className="flex gap-12 flex-wrap p-8 border-ctp-overlay0/50 border rounded-lg">
                {groupContents.map((release: IUnresolvedRelease) => (
                  <Suspense key={release.slug}
                    fallback={
                      <div className="w-[128px] h-[128px] bg-gray-500 animate-pulse"></div>
                    }
                  >
                    {view == DiscographyView.GRIDLIST && (
                      <ReleaseCard release={release} />
                    )}
                    {view == DiscographyView.ROW && (
                      <ReleaseRow release={release} />
                    )}
                  </Suspense>
                ))}
              </div>
            </div>
          );
        })}
    </Fragment>
  );
}
